import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { services } from '@/mocks/productsData';

function SectionHeader({ title, subtitle, visible }: { title: string; subtitle: string; visible: boolean }) {
  return (
    <div className="mb-8 md:mb-12">
      <h2
        className={`text-xl md:text-3xl font-bold text-primary mb-2 transition-all duration-500 delay-100 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        {title}
      </h2>
      <p
        className={`text-sm md:text-base text-text-muted transition-all duration-500 delay-200 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        {subtitle}
      </p>
    </div>
  );
}

function AnimatedSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {typeof children === 'function' ? children(visible) : children}
    </div>
  );
}

export default function Products() {
  return (
    <main>
      {/* Page Header */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] overflow-hidden">
        {/* 网格纹理 */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* 右上光晕 */}
        <div
          className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full opacity-20 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,174,239,0.4) 0%, transparent 70%)' }}
        />
        {/* 左下光晕 */}
        <div
          className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(0,174,239,0.3) 0%, transparent 70%)' }}
        />
        {/* 浮动小圆点 */}
        <div className="absolute top-20 right-[20%] w-2 h-2 bg-accent/40 rounded-full animate-pulse pointer-events-none" />
        <div className="absolute bottom-24 left-[15%] w-1.5 h-1.5 bg-white/30 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
        <div className="absolute top-40 left-[40%] w-1 h-1 bg-accent/30 rounded-full animate-pulse pointer-events-none" style={{ animationDelay: '2s' }} />
        {/* 装饰线条 */}
        <div className="absolute top-1/2 right-[10%] w-px h-[200px] bg-gradient-to-b from-transparent via-accent/20 to-transparent pointer-events-none" />
        <div className="absolute top-1/4 left-[8%] w-[100px] h-px bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              一站式营销数字化服务
            </h1>
            <p className="text-sm md:text-base text-white/60 leading-relaxed">
              从战略咨询到系统落地，从标准产品到定制开发，融慧数科为快消品牌提供一站式营销数字化服务。
            </p>
          </div>
        </div>
      </section>

      {/* Service 1: Consulting */}
      <AnimatedSection className="py-16 md:py-24 bg-white">
        {(visible: boolean) => (
          <div className="w-full px-4 md:px-8 lg:px-12">
            <SectionHeader title={services[0].title} subtitle={services[0].subtitle} visible={visible} />
            <div
              className={`max-w-3xl mx-auto transition-all duration-500 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p className="text-sm md:text-base text-text-secondary leading-relaxed mb-6">
                {services[0].description}
              </p>
              <div className="bg-surface rounded-xl p-5 md:p-6 mb-6">
                <h4 className="text-sm font-semibold text-primary mb-3">服务内容</h4>
                <div className="flex flex-wrap gap-2">
                  {services[0].items?.map((item, i) => (
                    <span key={i} className="px-3 py-1.5 bg-white border border-gray-100 rounded-lg text-sm text-text-secondary">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-3 bg-accent/5 rounded-xl p-4">
                <i className="ri-user-search-line text-accent text-lg mt-0.5" />
                <div>
                  <span className="text-sm font-semibold text-primary">适合谁：</span>
                  <span className="text-sm text-text-secondary">{services[0].target}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </AnimatedSection>

      {/* Service 2: Operation Support */}
      <AnimatedSection className="py-16 md:py-24 bg-surface">
        {(visible: boolean) => (
          <div className="w-full px-4 md:px-8 lg:px-12">
            <SectionHeader title={services[1].title} subtitle={services[1].subtitle} visible={visible} />
            <div
              className={`max-w-3xl mx-auto transition-all duration-500 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p className="text-sm md:text-base text-text-secondary leading-relaxed mb-6">
                {services[1].description}
              </p>
              <div className="bg-white rounded-xl p-5 md:p-6 mb-6">
                <h4 className="text-sm font-semibold text-primary mb-3">服务内容</h4>
                <div className="flex flex-wrap gap-2">
                  {services[1].items?.map((item, i) => (
                    <span key={i} className="px-3 py-1.5 bg-surface border border-gray-100 rounded-lg text-sm text-text-secondary">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-3 bg-accent/5 rounded-xl p-4">
                <i className="ri-user-search-line text-accent text-lg mt-0.5" />
                <div>
                  <span className="text-sm font-semibold text-primary">适合谁：</span>
                  <span className="text-sm text-text-secondary">{services[1].target}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </AnimatedSection>

      {/* Service 3: SaaS */}
      <AnimatedSection className="py-16 md:py-24 bg-white">
        {(visible: boolean) => (
          <div className="w-full px-4 md:px-8 lg:px-12">
            <SectionHeader title={services[3].title} subtitle={services[3].subtitle} visible={visible} />
            <div
              className={`max-w-4xl mx-auto transition-all duration-500 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p className="text-sm md:text-base text-text-secondary leading-relaxed mb-8">
                {services[3].description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 mb-8">
                {services[3].features?.map((feat, i) => (
                  <div
                    key={i}
                    className={`bg-white rounded-xl p-5 border border-gray-100 hover:border-accent/30 transition-all duration-300 ${
                      visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}
                    style={{ transitionDelay: `${400 + i * 100}ms` }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-primary rounded-lg mb-3">
                      <i className={`${feat.icon} text-white text-lg`} />
                    </div>
                    <h4 className="text-base font-semibold text-primary mb-1">{feat.title}</h4>
                    <p className="text-sm text-text-secondary leading-relaxed">{feat.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </AnimatedSection>

      {/* Service 4: Platform Development */}
      <AnimatedSection className="py-16 md:py-24 bg-surface">
        {(visible: boolean) => (
          <div className="w-full px-4 md:px-8 lg:px-12">
            <SectionHeader title={services[2].title} subtitle={services[2].subtitle} visible={visible} />
            <div
              className={`max-w-4xl mx-auto transition-all duration-500 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p className="text-sm md:text-base text-text-secondary leading-relaxed mb-8">
                {services[2].description}
              </p>

              {/* Module Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-8">
                {services[2].modules?.map((mod, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 md:p-6 border border-gray-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary rounded-lg">
                        <i className={`${i === 0 ? 'ri-qr-scan-line' : 'ri-link-m'} text-white`} />
                      </div>
                      <div>
                        <h4 className="text-base font-semibold text-primary">{mod.name}</h4>
                        <span className="text-xs text-accent">{mod.desc}</span>
                      </div>
                    </div>
                    <p className="text-sm text-text-secondary leading-relaxed">{mod.detail}</p>
                  </div>
                ))}
              </div>

              {/* TP + CP Table */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 bg-primary text-white text-sm font-semibold">
                  TP+CP双轮驱动策略
                </div>
                <div className="grid grid-cols-2 divide-x divide-gray-100">
                  <div className="p-5">
                    <h5 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-accent rounded-full" />
                      TP · 渠道推力
                    </h5>
                    <ul className="space-y-2">
                      {services[2].tpcp?.tp.map((item, i) => (
                        <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                          <i className="ri-check-line text-accent mt-0.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-5">
                    <h5 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-accent rounded-full" />
                      CP · 消费者拉力
                    </h5>
                    <ul className="space-y-2">
                      {services[2].tpcp?.cp.map((item, i) => (
                        <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                          <i className="ri-check-line text-accent mt-0.5 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </AnimatedSection>

      {/* Service 5: Data Operations */}
      <AnimatedSection className="py-16 md:py-24 bg-white">
        {(visible: boolean) => (
          <div className="w-full px-4 md:px-8 lg:px-12">
            <SectionHeader title={services[4].title} subtitle={services[4].subtitle} visible={visible} />
            <div
              className={`max-w-3xl mx-auto transition-all duration-500 delay-300 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p className="text-sm md:text-base text-text-secondary leading-relaxed mb-6">
                {services[4].description}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {services[4].items?.map((item, i) => (
                  <div
                    key={i}
                    className="bg-surface rounded-xl p-4 text-center border border-gray-100 hover:border-accent/30 transition-colors"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-accent/10 rounded-full mx-auto mb-2">
                      <i className="ri-database-2-line text-accent" />
                    </div>
                    <span className="text-sm font-medium text-primary">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </AnimatedSection>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-primary">
        <div className="w-full px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-xl md:text-3xl font-bold text-white mb-4">
            不确定哪种服务适合您？
          </h2>
          <p className="text-sm md:text-base text-white/60 mb-8 max-w-xl mx-auto">
            我们的顾问团队将根据您的业务现状，推荐最适合的数字化解决方案。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 bg-accent text-white font-semibold rounded-lg hover:bg-accent-light transition-colors"
          >
            <i className="ri-calendar-check-line" />
            预约免费咨询
          </Link>
        </div>
      </section>
    </main>
  );
}